import {
  ActivityIndicator,
  FlatList,
  SectionList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import React, { useEffect, useState } from "react";
import { fp, spH, spV, wp } from "utils/normalizeV2";
import { Link, router, useLocalSearchParams } from "expo-router";

import { Colors } from "colors";
import ParentScrollView from "components/ParentScrollView";
import VText from "components/VText";
import { getHomeSearchBrands } from "appRedux/reducers/homeSlice";
import getItemLayout from "utils/getItemLayout";
import { useDispatch, useSelector } from "react-redux";
import { useRef } from "react";
import Heading from "components/Heading";
import { encodeFiltersToUrl } from "utils/filterUtils";
import useSearch from "utils/hooks/useSearch";

const HEADER_HEIGHT = 56.5;

const Designers = () => {
  const [groupedData, setGroupedData] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alphabet, setAlphabet] = useState([]);
  const [selectedAplhabet, setSelectedAplhabet] = useState("");
  const local = useLocalSearchParams();
  const gender = local?.gender ?? "female";
  const _sectionRef = useRef();

  const allFilters = useSelector((state) => state.FilterReducer.filters || []);

  const dispatch = useDispatch();

  useEffect(() => {
    setLoading(true);
    dispatch(getHomeSearchBrands({ gender: gender }))
      .then((res) => {
        if (
          res?.payload?.data?.data &&
          Array.isArray(res?.payload?.data?.data)
        ) {
          const brands = res?.payload?.data?.data;
          setBrands(brands);
          const groupedDataTemp = brands.reduce((result, item) => {
            const firstLetter = item?.brandName?.[0]?.toUpperCase();
            if (!result[firstLetter]) {
              result[firstLetter] = [];
            }
            result[firstLetter].push(item);
            return result;
          }, {});
          const sections = Object.keys(groupedDataTemp)
            .sort()
            .map((key) => ({
              title: key,
              data: groupedDataTemp[key],
            }));
          setGroupedData(groupedDataTemp);
          setSectionData(sections);
          setAlphabet(Object.keys(groupedDataTemp).sort());
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
      });
  }, []);

  const buildGetItemLayout = getItemLayout({
    getItemHeight: (data, index) => {
      const numRows = Math.ceil(sectionData[index]?.data?.length / 4);
      const rowHeight = 50; // Height of each row, adjust as needed
      return numRows * rowHeight + 7;
    },
    getSeparatorHeight: () => 0, // Ensure separator height is set correctly
    getSectionHeaderHeight: () => HEADER_HEIGHT, // Correctly set the height of section headers
    getSectionFooterHeight: () => 0, // Ensure footer height is set if used
  });

  const scrollToSection = (sectionIndex) => {
    if (_sectionRef?.current && Array.isArray(sectionData)) {
      if (
        sectionIndex >= 0 &&
        sectionIndex < sectionData.length &&
        sectionData[sectionIndex].data.length > 0
      ) {
        _sectionRef.current.scrollToLocation({
          sectionIndex: sectionIndex,
          itemIndex: 0, // Ensure there is always a valid item index
          animated: true,
          viewPosition: 0.5, // Centers the item
          viewOffset: 50, // Adjust this value if needed to offset the scroll position
        });
      } else {
        console.warn(
          `Invalid section index: ${sectionIndex}. Total sections: ${sectionData.length}`
        );
      }
    } else {
      console.warn(
        "SectionList ref is not available or sectionData is not an array"
      );
    }
  };

  const renderAlphabetItem = ({ item }) => (
    <TouchableOpacity
      style={styles.alphabetItem}
      onPress={() => {
        setSelectedAplhabet(item);
        const sectionIndex = sectionData.findIndex(
          (section) => section.title === item
        );
        if (sectionIndex !== -1) {
          scrollToSection(sectionIndex);
        }
      }}
    >
      <VText
        style={[
          styles.alphabetText,
          { fontWeight: selectedAplhabet == item ? "700" : "350" },
        ]}
      >
        {item}
      </VText>
    </TouchableOpacity>
  );

  const renderItem = ({ item, index, section }) => {
    const numRows = Math.ceil(groupedData?.[section?.title]?.length / 4); // Assuming 4 brands per row
    const rowHeight = 50; // Height of each row, adjust as needed
    const height = numRows * rowHeight;

    return (
      <View style={[styles.row, { height: height }]}>
        {groupedData?.[section?.title]?.map((brand, idx) => {
          let link = encodeFiltersToUrl(
            {
              selected: {
                2: [
                  {
                    title: brand?.brandName?.replaceAll("%", "*percent*"),
                    id: Number(brand?.brandId),
                    options: [],
                  },
                ],
              },
            },
            allFilters
          );
          return (
            <Link
              key={brand.brandId}
              style={styles.brandItem}
              href={`${gender == 'men' ? '/men' : ''}/shop/new-arrivals?&filters=${link}&page=1&title=${brand?.brandName?.replaceAll(
                "%",
                " "
              )}`}
            >
              <VText
                style={styles.brandText}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {brand.brandName?.toUpperCase()}
              </VText>
            </Link>
          );
        })}
      </View>
    );
  };

  return (
    <ParentScrollView>
      <View style={styles.container}>
        <Heading type="h1" style={styles.title}>
          Designers (A-Z)
        </Heading>

        {loading && (
          <ActivityIndicator
            style={{
              marginTop: spV(100),
            }}
            size="large"
            color={Colors.black}
          />
        )}
        {!loading && (
          <>
            <View style={{ alignSelf: "center", marginBottom: spV(20) }}>
              <FlatList
                horizontal
                contentContainerStyle={{
                  width: wp(1040),
                  justifyContent: "space-between",
                  paddingBottom: spV(16),
                }}
                data={[...alphabet]}
                renderItem={renderAlphabetItem}
                keyExtractor={(item) => item}
                style={{}}
                showsVerticalScrollIndicator={false}
              />
              <SectionList
                nestedScrollEnabled={true}
                style={{ maxHeight: 700 }}
                ref={_sectionRef}
                getItemLayout={buildGetItemLayout}
                onScrollToIndexFailed={({ index, averageItemLength }) => {
                  try {
                    _sectionRef?.current?.scrollToOffset({
                      offset: index * averageItemLength,
                      animated: true,
                    });
                  } catch (err) {
                    console.log("err", err);
                  }
                }}
                sections={Object.keys(groupedData)
                  .sort()
                  .map((key) => ({
                    title: key,
                    data: [{}],
                  }))}
                keyExtractor={(item, index) => item + index}
                contentContainerStyle={{
                  width: wp(1040),
                  justifyContent: "space-between",
                  paddingBottom: 200,
                }}
                renderItem={renderItem}
                renderSectionHeader={({ section: { title } }) => (
                  <VText style={styles.sectionHeader}>{title}</VText>
                )}
              />
            </View>
          </>
        )}
      </View>
    </ParentScrollView>
  );
};

export default Designers;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: spV(32),
  },
  title: {
    textAlign: "center",
    fontSize: fp(28),
    fontFamily: "LibreBodoni",
    fontWeight: "350",
    marginBottom: spV(32),
  },
  contentContainer: {
    flexDirection: "row",
  },

  alphabetItem: {},
  alphabetText: {
    fontSize: 12,
    fontWeight: "350",
    color: "#212427",
  },

  row: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  brandItem: {
    width: "25%", // 4 items per row
  },
  brandText: {
    fontSize: fp(14),
    fontFamily: "JosefinSans",
    fontWeight: "350",
    color: "#212427",
    textAlign: "left",
  },
  sectionHeader: {
    fontFamily: "JosefinSans",
    fontWeight: "550",
    fontSize: fp(20),
    fontWeight: "900",
    color: "#212427",
    paddingTop: spV(16),
  },
  listContent: {
    paddingBottom: 200,
  },
});
